import React, { useState, useContext, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Carousel,
  Modal,
} from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaWhatsapp,
  FaShareAlt,
  FaCopy,
  FaCheck,
  FaFacebook,
  FaTwitter,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./home.css";
import { CartContext } from "./CartContext";
import PaginationComponent from "../components/Forms";
import config from "../config/config";
import translateToTamil from "../config/tamilTranslator";
import defaultLogo from "../assets/zen_logo.png";
import defaultBanner from "../assets/zen_banner.png";
import LeadCaptureModal from "../components/LeadCaptureModal";
import EnquiryQuoteModal from "../components/EnquiryQuoteModal";

const Home = () => {
  const { API_DOMAIN, subdomain } = config;
  const { cart, addToCart, increaseQty, decreaseQty, setQty, removeItem, storeTheme, setStoreTheme } =
    useContext(CartContext);

  // ------------------ COMPANY DETAILS STATE --------------------
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyError, setCompanyError] = useState(null);
  // console.log("Company :", company);
  // ------------------ PRODUCTS & CATEGORY STATE ----------------
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 100;
  const navigate = useNavigate();

  // ------------------ PRODUCT SHARE STATE & HELPERS ----------------
  const [selectedShareProduct, setSelectedShareProduct] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedQuoteProduct, setSelectedQuoteProduct] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const getProductShareUrl = (prod) => {
    if (!prod) return window.location.href;
    const prodId = prod.product_id || prod.id;
    return `${API_DOMAIN}/share.php?p=${encodeURIComponent(prodId)}&c=${encodeURIComponent(subdomain)}`;
  };

  const handleShareProduct = (prod, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!prod) return;

    const shareUrl = getProductShareUrl(prod);
    const storeTitle = company?.company_name || "Zen Online Store";
    const shareText = `Check out *${prod.product_name}* at ₹${prod.price} on ${storeTitle}!\n\nView details & order online:`;

    if (navigator.share) {
      navigator.share({
        title: `${prod.product_name} - ${storeTitle}`,
        text: shareText,
        url: shareUrl,
      }).catch((err) => {
        if (err.name !== 'AbortError') {
          setSelectedShareProduct(prod);
          setShowShareModal(true);
        }
      });
    } else {
      setSelectedShareProduct(prod);
      setShowShareModal(true);
    }
  };

  const handleCopyShareLink = (url) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2500);
      });
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    }
  };

  // Company logo fallback logic:
  // If company has uploaded a logo, use it; otherwise use defaultLogo
  const companyLogo = (company && company.img && company.img.trim() !== "" && company.img !== "null")
    ? company.img
    : defaultLogo;

  // Product picture fallback logic:
  // If product has a picture, use it.
  // If product has NO picture: if company uploaded a logo, show that company logo; otherwise show defaultLogo
  const getProductImage = (prod) => {
    if (prod && prod.product_img_url && prod.product_img_url.trim() !== "" && prod.product_img_url !== "null") {
      return prod.product_img_url;
    }
    return companyLogo;
  };

  // Banner fallback logic:
  // If company has uploaded custom banners, show them.
  // If no banner is uploaded, use defaultBanner.
  // When custom banners are uploaded, defaultBanner is NOT shown.
  const customBanners = Array.isArray(company?.banners)
    ? company.banners.filter(
        (b) => b && typeof b === "string" && b.trim() !== "" && b !== "null"
      )
    : [];
  const hasCustomBanners = customBanners.length > 0;
  const activeBanners = hasCustomBanners ? customBanners : [defaultBanner];

  // ------------------ FETCH COMPANY DETAILS --------------------
  useEffect(() => {
    const fetchCompany = async () => {
      setCompanyLoading(true);
      setCompanyError(null);

      try {
        const payload = { search_text: "company", company_id: subdomain };
        const url = `${API_DOMAIN}/company.php`;

        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        // console.log("Company fetch payload:", payload);
        if (!response.ok) throw new Error("Failed to fetch company details");

        const data = await response.json();

        // console.log("Company fetch response data:", data);

        if (data.head.code === 200) {
          const comp = data.body.companies[0];
          setCompany(comp);
          const theme = comp.store_ui_theme || "theme1";
          setStoreTheme(theme);
          localStorage.setItem("store_ui_theme", theme);
        } else {
          throw new Error(data.head.msg);
        }
      } catch (err) {
        setCompanyError(err.message);
      } finally {
        setCompanyLoading(false);
      }
    };

    fetchCompany();
  }, [API_DOMAIN, setStoreTheme, subdomain]);

  // ------------------ FETCH PRODUCTS + CATEGORIES --------------
  useEffect(() => {
    const fetchProductsAndCategories = async () => {
      setLoading(true);
      setError(null);

      const companyId = subdomain;
      const payload = { company_id: companyId, fetch_all: "true" };

      try {
        // 1. CATEGORY FETCH
        const categoryResponse = await fetch(`${API_DOMAIN}/category.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const categoryData = await categoryResponse.json();

        let apiCatsRaw = [];
        let apiCats = [];
        if (categoryData.head.code === 200 && categoryData.body.categories) {
          apiCatsRaw = categoryData.body.categories;
          apiCats = apiCatsRaw.map((cat) => {
            const catName = String(cat.category_name || "");
            const taName = cat.category_name_ta || cat.name_ta || cat.category_ta || translateToTamil(catName);
            return {
              id: String(cat.category_code),
              raw_id: cat.id,
              category_code: String(cat.category_code),
              category_id: String(cat.category_id || cat.id || cat.category_code),
              name: cat.category_name,
              category_name: cat.category_name,
              category_name_ta: taName,
              name_ta: taName,
              discount: parseFloat(cat.discount) || 0,
            };
          });
        }

        // 2. PRODUCT FETCH
        const productResponse = await fetch(`${API_DOMAIN}/product.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const productData = await productResponse.json();

        if (productData.head.code === 200) {
          let fetchedProducts = productData.body.products.map((product) => {
            const originalPrice = parseFloat(product.product_price) || 0;
            const productDisc = parseFloat(product.product_disc) || 0;
            const discountLocked = product.discount_locked === 1 || product.discount_locked === "1" || product.discount_locked === true;

            // Find category discount
            const catItem = apiCatsRaw.find(c => String(c.category_code) === String(product.category_code));
            const categoryDisc = catItem ? (parseFloat(catItem.discount) || 0) : 0;

            // Determine effective discount
            const effectiveDisc = (discountLocked || categoryDisc === 0) ? productDisc : categoryDisc;

            // Calculate final price
            const finalPrice = effectiveDisc > 0 ? (originalPrice - (originalPrice * effectiveDisc / 100)) : originalPrice;

            const productName = String(product.product_name != null ? product.product_name : "");
            const tamilProdName = product.product_name_ta || product.name_ta || translateToTamil(productName);
            const categoryName = String(product.category_name != null ? product.category_name : (product.category != null ? product.category : ""));
            const tamilCatName = product.category_name_ta || (catItem ? (catItem.category_name_ta || catItem.name_ta) : "") || translateToTamil(categoryName);

            return {
              id: product.id,
              product_id: product.product_id,
              product_name: productName,
              product_name_ta: tamilProdName,
              name_ta: tamilProdName,
              product_code: product.product_code,
              category_code: String(product.category_code),
              category_id: String(product.category_id || product.category_code),
              category_name: categoryName,
              category_name_ta: tamilCatName,
              category: categoryName,
              unit_code: product.unit_code || product.unit_name || product.unit || "",
              unit_name: product.unit_name || product.unit || "",
              original_price: originalPrice,
              discount: effectiveDisc,
              price: parseFloat(finalPrice.toFixed(2)) || 0,
              product_img_url: product.product_img_url || "",
              product_img_urls: product.product_img_urls || [product.product_img_url || ""],
              description: product.description || "",
              discount_locked: discountLocked,
              product_disc: productDisc,
              gst_percent: parseFloat(product.gst_percent) || 0,
              gst_type: (product.gst_type && String(product.gst_type).trim() !== "") ? String(product.gst_type).trim().toLowerCase() : (company?.default_gst_type || "inclusive"),
              hsn_code: product.hsn_code || "",
            };
          });

          // Sort products by ID in ascending order (1, 2, 3...)
          fetchedProducts.sort((a, b) => {
            const idA = parseInt(a.id, 10);
            const idB = parseInt(b.id, 10);
            if (!isNaN(idA) && !isNaN(idB)) {
              return idA - idB;
            }
            return String(a.id || "").localeCompare(String(b.id || ""), undefined, { numeric: true });
          });

          // Sort categories by ID in ascending order (1, 2, 3...)
          apiCats.sort((a, b) => {
            const valA = a.raw_id != null ? a.raw_id : a.id;
            const valB = b.raw_id != null ? b.raw_id : b.id;
            const idA = parseInt(valA, 10);
            const idB = parseInt(valB, 10);
            if (!isNaN(idA) && !isNaN(idB)) {
              return idA - idB;
            }
            return String(valA || "").localeCompare(String(valB || ""), undefined, { numeric: true });
          });

          setCategories([{ id: "all", name: "All" }, ...apiCats]);
          setProducts(fetchedProducts);
        } else {
          throw new Error(productData.head.msg);
        }
      } catch (err) {
        setError("Could not load products/categories — " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsAndCategories();
  }, [API_DOMAIN, subdomain]);

  // ------------------ FILTERING ------------------
  const cleanSearch = searchTerm.trim().toLowerCase();
  const filteredProducts = products.filter((p) => {
    const matchesCategory =
      selectedCategory === "all" ||
      String(p.category_code) === String(selectedCategory) ||
      String(p.category_id) === String(selectedCategory);

    const matchesSearch =
      !cleanSearch ||
      (p.product_name && String(p.product_name).toLowerCase().includes(cleanSearch)) ||
      (p.product_name_ta && String(p.product_name_ta).toLowerCase().includes(cleanSearch)) ||
      (String(p.product_code || "").toLowerCase().includes(cleanSearch)) ||
      (p.category_name && p.category_name.toLowerCase().includes(cleanSearch)) ||
      (p.category_name_ta && p.category_name_ta.toLowerCase().includes(cleanSearch)) ||
      (p.description && p.description.toLowerCase().includes(cleanSearch));

    return matchesCategory && matchesSearch;
  });

  // ------------------ PAGINATION ------------------
  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (p) => setCurrentPage(p);

  const cartTotalItems = cart.reduce((sum, i) => sum + i.qty, 0);
  const cartTotalPrice = cart.reduce((sum, i) => sum + i.price * i.qty, 0);

  const handleProductClick = (product) => {
    navigate("/product-details", { state: { product } });
  };

  // ------------------ MODULAR PRODUCT GRID RENDERING ------------------
  const renderProductGrid = (colProps = { xs: 6, sm: 4, md: 3, lg: 2 }) => {
    if (loading || error) return null;
    if (currentProducts.length === 0) {
      return (
        <Col xs={12} className="text-center my-5 no-products-msg">
          <p className="fs-5 text-muted">No products found matching your search.</p>
        </Col>
      );
    }

    return currentProducts.map((prod) => {
      const cartItem = cart.find((i) => i.id === prod.id);
      return (
        <Col
          key={prod.id}
          xs={colProps.xs}
          sm={colProps.sm}
          md={colProps.md}
          lg={colProps.lg}
          className="mb-4 custom-col fade-in-up"
        >
          <div
            className="product-card"
            onClick={() => handleProductClick(prod)}
          >
            <div className="product-img-container position-relative">
              <button
                type="button"
                className="product-share-btn"
                title={`Share ${prod.product_name}`}
                aria-label="Share product"
                onClick={(e) => handleShareProduct(prod, e)}
              >
                <FaShareAlt />
              </button>
              {storeTheme === "theme3" && (
                <span className="theme3-card-badge position-absolute px-2 py-1 text-dark fw-bold" style={{ top: '0', left: '0', zIndex: '2', fontSize: '9px', borderBottomRightRadius: '8px', backgroundColor: '#fbbf24' }}>
                  {prod.product_code}
                </span>
              )}
              <img
                src={getProductImage(prod)}
                alt={prod.product_name}
                className="product-img"
                onError={(e) => {
                  if (e.currentTarget.src !== companyLogo) {
                    e.currentTarget.src = companyLogo;
                  } else {
                    e.currentTarget.src = defaultLogo;
                  }
                }}
              />
            </div>
            <div className="product-details">
              <div className="product-main-info">
                <p className="product-name">{prod.product_name}</p>
                <p className="product-code">
                  ITEM CODE: {prod.product_code}
                </p>
              </div>

              <div className="product-footer d-flex justify-content-between align-items-center">
                <div className="product-price-wrapper">
                  <p className="product-price mb-0">₹ {prod.price}</p>
                  {(company?.gst_enabled === 1 || company?.gst_enabled === "1" || company?.gst_enabled === true) && prod.gst_percent > 0 && (
                    <span className="badge bg-light text-secondary border mt-1" style={{ fontSize: '10px' }}>
                      {prod.gst_type === 'exclusive' ? `+ ${prod.gst_percent}% GST` : `Incl. ${prod.gst_percent}% GST`}
                    </span>
                  )}
                  {prod.discount > 0 && (
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <span className="product-original-price text-decoration-line-through text-muted small" style={{ fontSize: '11px' }}>₹ {prod.original_price}</span>
                      <span className="product-discount-badge text-danger fw-bold" style={{ fontSize: '11px' }}>{prod.discount}% OFF</span>
                    </div>
                  )}
                </div>

                {cartItem ? (
                  <div className="quantity-controls">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        cartItem.qty === 1
                          ? removeItem(prod.id)
                          : decreaseQty(prod.id);
                      }}
                    >
                      −
                    </Button>
                    <span className="mx-2 quantity-number">{cartItem.qty}</span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        increaseQty(prod.id);
                      }}
                    >
                      +
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    className="add-to-cart-btn"
                    variant="outline-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart(prod);
                    }}
                  >
                    + Add
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Col>
      );
    });
  };

  // ----------------------------------------------------
  return (
    <div className={`home-section ${storeTheme}`}>
      {/* Advance Meta Tags via React Helmet */}
      <Helmet>
        <title>{company?.company_name ? `${company.company_name} - Online Store` : "Zen Online Store"}</title>
        <meta name="description" content={company?.description || `Welcome to ${company?.company_name || 'our online store'}. Shop online with best prices and instant delivery.`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={company?.company_name || "Zen Online Store"} />
        <meta property="og:title" content={company?.company_name ? `${company.company_name} - Online Store` : "Zen Online Store"} />
        <meta property="og:description" content={company?.description || `Shop products online from ${company?.company_name || 'Zen Online Store'}.`} />
        <meta property="og:image" content={companyLogo || defaultLogo} />
        <meta property="og:image:secure_url" content={companyLogo || defaultLogo} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={company?.company_name || "Zen Online Store"} />
        <meta name="twitter:description" content={company?.description || "Online Store"} />
        <meta name="twitter:image" content={companyLogo || defaultLogo} />
      </Helmet>
      {/* Top Scrolling Ad Content (Active on all 4 themes, only if configured in app) */}
      {company?.ad_content && company.ad_content.trim() !== "" && (
        <div className={`store-ad-ticker ticker-${storeTheme}`}>
          <div className="store-ad-badge">
            <span className="badge-pulse"></span>
            <span className="badge-text-desktop">📢 OFFERS & UPDATES</span>
            <span className="badge-text-mobile">📢 OFFERS</span>
          </div>
          <div className="store-ad-track">
            <div className="store-ad-marquee">
              {[...Array(8)].map((_, i) => (
                <React.Fragment key={i}>
                  <span className="store-ad-text">{company.ad_content}</span>
                  <span className="store-ad-sep">✦</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
      <Container className="home-main-container">
        {/* ------------- LOADING / ERROR OF COMPANY ------------- */}
        {companyLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant={storeTheme === "theme3" ? "warning" : "primary"} />
            <p className="mt-2 text-muted">Loading store details...</p>
          </div>
        )}
        {companyError && <Alert variant="danger">Error loading store: {companyError}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        {company && (
          <>
            {/* ==========================================================
                THEME 1: CLASSIC GRID LAYOUT (DEFAULT - SPLIT HERO)
                ========================================================== */}
            {storeTheme === "theme1" && (
              <div className="theme1-layout">
                {/* Hero Split Row */}
                <Row className="align-items-start justify-content-center mb-4 g-4">
                  {/* Left Column: Welcome Card */}
                  <Col xs={12} lg={5} className="d-flex">
                    <div className="theme1-welcome-card p-4 rounded-4 bg-white border shadow-sm w-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="d-flex align-items-center mb-3">
                          <img
                            src={companyLogo}
                            alt={company.company_name || "Company Logo"}
                            className="theme1-logo me-3"
                            onError={(e) => { e.currentTarget.src = defaultLogo; }}
                          />
                          <div>
                            <h2 className="theme1-title m-0">{company.company_name}</h2>
                            <span className="text-muted" style={{ fontSize: '11px', fontWeight: '600' }}>Official Catalog</span>
                          </div>
                        </div>
                        <p className="theme1-info mb-2 text-secondary" style={{ fontSize: '13px', lineHeight: '1.4' }}>
                          <FaMapMarkerAlt className="text-primary me-2" />
                          {company.address?.split("\n").map((line, i) => (
                            <span key={i}>{line}<br /></span>
                          ))}
                        </p>
                        <p className="theme1-info mb-2 text-secondary" style={{ fontSize: '13px' }}>
                          <FaEnvelope className="text-primary me-2" /> {company.city}, {company.state}
                        </p>
                        <p className="theme1-info mb-0 text-secondary" style={{ fontSize: '13px' }}>
                          <FaPhoneAlt className="text-primary me-2" /> Contact: {company.mobile}
                        </p>
                      </div>

                      <div className="d-flex gap-2 mt-4">
                        <button className="btn btn-primary rounded-pill px-4" onClick={() => {
                          const el = document.getElementById("catalog-section");
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}>
                          Explore Shop
                        </button>
                        <a href={`tel:${company.mobile}`} className="btn btn-outline-secondary rounded-pill px-3 d-flex align-items-center justify-content-center">
                          <FaPhoneAlt />
                        </a>
                        {company.mobile && (
                          <a
                            href={`https://wa.me/${String(company.mobile || company.phone || "").replace(/[^0-9]/g, "").length === 10 ? '91' + String(company.mobile || company.phone || "").replace(/[^0-9]/g, "") : String(company.mobile || company.phone || "").replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn rounded-pill px-3 d-flex align-items-center justify-content-center text-white"
                            style={{ backgroundColor: "#25D366" }}
                          >
                            <FaWhatsapp />
                          </a>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Right Column: Banner Carousel */}
                  <Col xs={12} lg={7}>
                    <Carousel
                      interval={3500}
                      controls={activeBanners.length > 1}
                      indicators={activeBanners.length > 1}
                      className="theme1-banner-carousel shadow-sm rounded-4 overflow-hidden border"
                    >
                      {activeBanners.map((url, idx) => (
                        <Carousel.Item key={idx}>
                          <img
                            className="d-block w-100 theme1-banner-image"
                            src={url}
                            alt={`Banner ${idx + 1}`}
                            onError={(e) => {
                              if (e.currentTarget.src !== defaultBanner) {
                                e.currentTarget.src = defaultBanner;
                              }
                            }}
                          />
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  </Col>
                </Row>

                <div id="catalog-section" className="pt-2">
                  <hr />
                </div>

                {/* Listings Navigation Bar (Sticky on Mobile & Laptop) */}
                <div className="theme-filter-sticky-wrapper theme1-sticky-filter mb-4">
                  <Row className="align-items-center g-3">
                    <Col xs={12} md={4}>
                      <Form.Control
                        type="text"
                        placeholder="Search items..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="theme1-search-input mb-2 mb-md-0"
                      />
                    </Col>
                    <Col xs={12} md={8}>
                      <div className="category-scroll-container">
                        {categories.map((cat) => (
                          <Button
                            key={cat.id}
                            variant={selectedCategory === cat.id ? "primary" : "outline-secondary"}
                            className="category-btn rounded-pill px-3"
                            onClick={() => {
                              setSelectedCategory(cat.id);
                              setCurrentPage(1);
                            }}
                          >
                            {cat.name}
                          </Button>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Main Product Grid */}
                <Row className="product-grid">
                  {renderProductGrid({ xs: 6, sm: 4, md: 3, lg: 2 })}
                </Row>
              </div>
            )}

            {/* ==========================================================
                THEME 2: MODERN CYAN MINIMAL (SIDEBAR & SPLIT LAYOUT)
                ========================================================== */}
            {storeTheme === "theme2" && (
              <div className="theme2-layout">
                {/* Clean Flat Header Row */}
                <Row className="theme2-header align-items-center mb-4 py-3 border-bottom">
                  <Col xs={8} md={6} className="d-flex align-items-center">
                    <img
                      src={companyLogo}
                      alt={company.company_name || "Store Logo"}
                      className="theme2-header-logo me-3"
                      onError={(e) => { e.currentTarget.src = defaultLogo; }}
                    />
                    <div>
                      <h2 className="theme2-header-title m-0">{company.company_name}</h2>
                      <span className="theme2-tagline text-muted d-none d-sm-inline">Fresh & Local Catalog</span>
                    </div>
                  </Col>
                  <Col xs={4} md={6} className="text-end theme2-header-actions">
                    <a href={`tel:${company.mobile}`} className="theme2-quick-call btn btn-outline-teal d-inline-flex align-items-center justify-content-center gap-1">
                      <FaPhoneAlt size={12} /> <span className="theme2-action-label">Call</span>
                    </a>
                    {company.mobile && (
                      <a
                        href={`https://wa.me/${String(company.mobile || company.phone || "").replace(/[^0-9]/g, "").length === 10 ? '91' + String(company.mobile || company.phone || "").replace(/[^0-9]/g, "") : String(company.mobile || company.phone || "").replace(/[^0-9]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="theme2-quick-wa btn btn-teal d-inline-flex align-items-center justify-content-center gap-1"
                      >
                        <FaWhatsapp size={14} /> <span className="theme2-action-label">WhatsApp</span>
                      </a>
                    )}
                  </Col>
                </Row>

                <Row>
                  {/* Left Column Sidebar - Desktop Only */}
                  <Col md={3} className="theme2-sidebar-col d-none d-md-block">
                    <div className="sticky-sidebar">
                      {/* Search Box Widget */}
                      <div className="sidebar-widget mb-4 p-3 rounded-4 bg-white border border-teal-light">
                        <h5 className="widget-title mb-3">Search Catalog</h5>
                        <Form.Control
                          type="text"
                          placeholder="Type to search..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="theme2-search-input"
                        />
                      </div>

                      {/* Categories List (Vertical List Widget) */}
                      <div className="sidebar-widget mb-4 p-3 rounded-4 bg-white border border-teal-light">
                        <h5 className="widget-title mb-3">Shop Categories</h5>
                        <div className="vertical-categories d-flex flex-column gap-2">
                          {categories.map((cat) => (
                            <button
                              key={cat.id}
                              className={`category-list-item py-2 px-3 rounded-3 text-start border-0 transition-all ${selectedCategory === cat.id
                                ? "category-active-item"
                                : "category-inactive-item"
                                }`}
                              onClick={() => {
                                setSelectedCategory(cat.id);
                                setCurrentPage(1);
                              }}
                            >
                              {cat.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Store Details Widget */}
                      <div className="sidebar-widget contact-widget p-3 rounded-4 bg-teal-ultra-light border-0">
                        <h5 className="widget-title mb-3">Visit Us</h5>
                        <p className="contact-address mb-2 text-muted" style={{ fontSize: "12px", lineHeight: "1.4" }}>
                          <FaMapMarkerAlt className="text-teal me-2" />
                          {company.address}
                        </p>
                        <p className="contact-info-text mb-2 text-muted" style={{ fontSize: "12px" }}>
                          <FaEnvelope className="text-teal me-2" /> {company.city}, {company.state}
                        </p>
                        <p className="contact-info-text mb-0 text-muted" style={{ fontSize: "12px" }}>
                          <FaPhoneAlt className="text-teal me-2" /> {company.mobile}
                        </p>
                      </div>
                    </div>
                  </Col>

                  {/* Right Column Content */}
                  <Col xs={12} md={9} className="theme2-main-col">
                    {/* Banners Carousel */}
                    <div className="theme2-carousel-container mb-4">
                      <Carousel
                        interval={3500}
                        controls={activeBanners.length > 1}
                        indicators={activeBanners.length > 1}
                        className="theme2-banner-carousel shadow-sm rounded-4 border-0"
                      >
                        {activeBanners.map((url, idx) => (
                          <Carousel.Item key={idx}>
                            <img
                              className="d-block w-100 rounded-4 theme2-banner-image"
                              src={url}
                              alt={`Banner ${idx + 1}`}
                              onError={(e) => {
                                if (e.currentTarget.src !== defaultBanner) {
                                  e.currentTarget.src = defaultBanner;
                                }
                              }}
                            />
                          </Carousel.Item>
                        ))}
                      </Carousel>
                    </div>

                    {/* Search & Categories Bar (Sticky on Mobile & Laptop) */}
                    <div className="theme-filter-sticky-wrapper theme2-sticky-filter mb-4 px-2">
                      <Row className="align-items-center g-2">
                        <Col xs={12} md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Search items..."
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="theme2-search-input mb-2 mb-md-0"
                          />
                        </Col>
                        <Col xs={12} md={7}>
                          <div className="category-scroll-container">
                            {categories.map((cat) => (
                              <Button
                                key={cat.id}
                                variant={selectedCategory === cat.id ? "teal" : "outline-teal"}
                                className="category-btn rounded-pill px-3"
                                onClick={() => {
                                  setSelectedCategory(cat.id);
                                  setCurrentPage(1);
                                }}
                              >
                                {cat.name}
                              </Button>
                            ))}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {/* Active Section Info Header */}
                    <div className="theme2-section-header mb-3 px-2 d-flex justify-content-between align-items-center">
                      <h4 className="theme2-section-title m-0">
                        {categories.find((c) => c.id === selectedCategory)?.name || "Catalog"}
                      </h4>
                      <span className="theme2-item-count text-muted">{totalItems} items available</span>
                    </div>

                    {/* Products Grid for Theme 2 */}
                    <Row className="product-grid px-1">
                      {renderProductGrid({ xs: 6, sm: 4, md: 4, lg: 3 })}
                    </Row>

                    {/* Mobile Footer Store Location Card */}
                    <div className="d-block d-md-none mt-5 p-4 rounded-4 bg-white border border-teal-light text-center">
                      <h5 className="mb-3 text-teal font-weight-bold">Store Location & Contacts</h5>
                      <p className="mb-2"><FaMapMarkerAlt className="text-teal me-2" /> {company.address}</p>
                      <p className="mb-2"><FaEnvelope className="text-teal me-2" /> {company.city}, {company.state}</p>
                      <p className="mb-0"><FaPhoneAlt className="text-teal me-2" /> {company.mobile}</p>
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {/* ==========================================================
                THEME 3: PREMIUM DARK & GOLD (LUXURY MODE - SPLIT HERO)
                ========================================================== */}
            {storeTheme === "theme3" && (
              <div className="theme3-layout">
                {/* Luxury Split Hero Header */}
                <Row className="align-items-start justify-content-center mb-5 g-4">
                  {/* Left Column: Glassmorphic Store Details Panel */}
                  <Col xs={12} lg={5} className="d-flex">
                    <div className="theme3-hero-panel p-4 rounded-4 w-100 d-flex flex-column justify-content-between border border-gold-dark text-center text-lg-start">
                      <div className="d-flex flex-column align-items-center align-items-lg-start">
                        <div className="d-flex align-items-center mb-4">
                          <img
                            src={companyLogo}
                            alt={company.company_name || "Premium Logo"}
                            className="theme3-hero-logo me-3"
                            onError={(e) => { e.currentTarget.src = defaultLogo; }}
                          />
                          <div>
                            <h1 className="theme3-store-name m-0" style={{ fontSize: '26px' }}>{company.company_name}</h1>
                            <p className="theme3-store-tagline m-0 text-gold" style={{ fontSize: '10px' }}>Exquisite Premium Collection</p>
                          </div>
                        </div>

                        <p className="theme3-info-text mb-2 text-light-gray" style={{ fontSize: '13px' }}>
                          <FaMapMarkerAlt className="text-gold me-2" />
                          {company.address}
                        </p>
                        <p className="theme3-info-text mb-2 text-light-gray" style={{ fontSize: '13px' }}>
                          <FaEnvelope className="text-gold me-2" /> {company.city}, {company.state}
                        </p>
                        <p className="theme3-info-text mb-0 text-light-gray" style={{ fontSize: '13px' }}>
                          <FaPhoneAlt className="text-gold me-2" /> Call: {company.mobile}
                        </p>
                      </div>

                      {/* Luxury Quick Actions */}
                      <div className="d-flex gap-3 mt-4 justify-content-center justify-content-lg-start">
                        <button className="btn btn-gold rounded-pill px-4 text-dark font-weight-bold" onClick={() => {
                          const el = document.getElementById("catalog-section-theme3");
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}>
                          Shop Catalog
                        </button>
                        <a href={`tel:${company.mobile}`} className="theme3-contact-icon-btn">
                          <FaPhoneAlt size={14} />
                        </a>
                        {company.mobile && (
                          <a
                            href={`https://wa.me/${String(company.mobile || company.phone || "").replace(/[^0-9]/g, "").length === 10 ? '91' + String(company.mobile || company.phone || "").replace(/[^0-9]/g, "") : String(company.mobile || company.phone || "").replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="theme3-contact-icon-btn theme3-wa"
                          >
                            <FaWhatsapp size={16} />
                          </a>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} lg={7}>
                    <Carousel
                      interval={4000}
                      fade
                      controls={activeBanners.length > 1}
                      indicators={activeBanners.length > 1}
                      className="theme3-hero-carousel shadow-lg rounded-4 overflow-hidden border border-gold-dark"
                    >
                      {activeBanners.map((url, idx) => (
                        <Carousel.Item key={idx} className="position-relative">
                          <img
                            className="d-block w-100 theme3-banner-image"
                            src={url}
                            alt={`Banner ${idx + 1}`}
                            onError={(e) => {
                              if (e.currentTarget.src !== defaultBanner) {
                                e.currentTarget.src = defaultBanner;
                              }
                            }}
                          />
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  </Col>
                </Row>

                <div id="catalog-section-theme3" className="pt-2"></div>

                {/* Luxury Search & Categories (Sticky on Mobile & Laptop) */}
                <div className="theme-filter-sticky-wrapper theme3-sticky-filter mb-4 px-2">
                  <Row className="justify-content-center mb-2">
                    <Col xs={12} md={8} lg={6}>
                      <div className="theme3-search-wrapper position-relative">
                        <Form.Control
                          type="text"
                          placeholder="Search our fine catalog..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="theme3-search-input text-center"
                        />
                        <div className="theme3-search-border-bottom" />
                      </div>
                    </Col>
                  </Row>

                  {/* Golden Categories Selection */}
                  <div className="theme3-category-wrapper text-center">
                    <div className="category-scroll-container justify-content-md-center px-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          className={`theme3-category-btn px-4 py-2 mx-1 transition-all ${selectedCategory === cat.id ? "active" : ""
                            }`}
                          onClick={() => {
                            setSelectedCategory(cat.id);
                            setCurrentPage(1);
                          }}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Section Title */}
                <div className="theme3-grid-header border-bottom border-gold-dark pb-3 mb-4 px-2 d-flex justify-content-between align-items-center">
                  <h3 className="theme3-section-title m-0">
                    {categories.find((c) => c.id === selectedCategory)?.name || "All Items"}
                  </h3>
                  <span className="theme3-meta-count text-gold">{totalItems} curated items</span>
                </div>

                {/* Product Grid */}
                <Row className="product-grid px-1">
                  {renderProductGrid({ xs: 6, sm: 4, md: 3, lg: 2.4 })}
                </Row>

                {/* Elegant Footer Details */}
                <div className="theme3-details-footer mt-5 py-5 text-center border-top border-gold-dark">
                  <p className="mb-2"><FaMapMarkerAlt className="text-gold me-2" /> {company.address}</p>
                  <p className="mb-2"><FaEnvelope className="text-gold me-2" /> {company.city}, {company.state}</p>
                  <p className="mb-0"><FaPhoneAlt className="text-gold me-2" /> Contact: {company.mobile}</p>
                </div>
              </div>
            )}

            {/* ==========================================================
                THEME 4: WHOLESALE PRICE LIST TABLE LAYOUT (NO IMAGE COLUMN)
                ========================================================== */}
            {storeTheme === "theme4" && (
              <div className="theme4-layout">
                {/* Full A4 Size Price List Poster Banner for Theme 4 */}
                {company && (
                  <div className="theme4-a4-banner-wrapper mb-4 text-center">
                    <div className="d-flex justify-content-center">
                      <div className="theme4-a4-banner-card shadow-sm rounded-4 overflow-hidden border bg-white" style={{ maxWidth: "850px", width: "100%" }}>
                        <Carousel
                          interval={4000}
                          controls={activeBanners.length > 1}
                          indicators={activeBanners.length > 1}
                          className="w-100"
                        >
                          {activeBanners.map((url, idx) => (
                            <Carousel.Item key={idx}>
                              <img
                                className="d-block w-100 theme4-a4-banner-img"
                                style={{ width: "100%", height: "auto", display: "block" }}
                                src={url}
                                alt={`Pricelist Banner ${idx + 1}`}
                                onError={(e) => {
                                  if (e.currentTarget.src !== defaultBanner) {
                                    e.currentTarget.src = defaultBanner;
                                  }
                                }}
                              />
                            </Carousel.Item>
                          ))}
                        </Carousel>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sticky Header Group for Theme 4 (Summary Bar + Search & Categories) */}
                <div className="theme4-sticky-wrapper mb-4">
                  {/* Summary Bar */}
                  <div className="theme4-top-bar shadow-sm mb-2 rounded-3">
                    <div className="theme4-summary-item">
                      <span className="theme4-summary-label">Total Product:</span>
                      <span className="theme4-summary-value">{products.length}</span>
                    </div>
                    <div className="theme4-summary-item">
                      <span className="theme4-summary-label">Net Rate Total :</span>
                      <span className="theme4-summary-value">
                        ₹{cart.reduce((sum, item) => sum + (parseFloat(item.price || item.original_price || 0) * item.qty), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="theme4-summary-item">
                      <span className="theme4-summary-label">Discount with Total:</span>
                      <span className="theme4-summary-value">
                        ₹{cart.reduce((sum, item) => {
                          const mrp = parseFloat(item.original_price || item.price || 0);
                          const netRate = parseFloat(item.price || 0);
                          const savings = mrp > netRate ? (mrp - netRate) : 0;
                          return sum + (savings * item.qty);
                        }, 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="theme4-cart-btn" onClick={() => navigate("/orderpage")}>
                      🛒
                      {cart.length > 0 && (
                        <span className="theme4-cart-badge">
                          {cart.reduce((total, item) => total + item.qty, 0)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Filter & Category Bar */}
                  <div className="theme4-filter-card p-2 p-md-3 bg-white rounded-4 shadow-sm border">
                    <Row className="align-items-center g-2">
                      <Col xs={12} md={4}>
                        <Form.Control
                          type="text"
                          placeholder="Search product..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="theme4-search-input rounded-pill px-3"
                        />
                      </Col>
                      <Col xs={12} md={8}>
                        <div className="category-scroll-container py-1">
                          <button
                            className={`btn btn-sm rounded-pill px-3 me-1 fw-semibold text-nowrap ${selectedCategory === "all" ? "btn-primary text-white shadow-sm" : "btn-outline-secondary"}`}
                            onClick={() => { setSelectedCategory("all"); setCurrentPage(1); }}
                          >
                            All ({products.length})
                          </button>
                          {categories
                            .filter((cat) => cat.name?.toLowerCase() !== "all")
                            .map((cat) => {
                              const isCatActive = selectedCategory === cat.id;
                              return (
                                <button
                                  key={cat.id}
                                  className={`btn btn-sm rounded-pill px-3 me-1 fw-semibold text-nowrap d-inline-flex align-items-center gap-1 ${isCatActive ? "btn-primary text-white shadow-sm" : "btn-outline-secondary"}`}
                                  onClick={() => { setSelectedCategory(cat.id); setCurrentPage(1); }}
                                >
                                  <span>{cat.name}</span>
                                  {cat.discount > 0 && (
                                    <span className={`badge rounded-pill ${isCatActive ? "bg-warning text-dark" : "bg-warning-subtle text-warning-emphasis"}`} style={{ fontSize: "10px" }}>
                                      {cat.discount}%
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>

                {/* Price List Table */}
                <div className="table-responsive theme4-table-container">
                  <table className="table table-bordered theme4-table text-center align-middle m-0">
                    <thead>
                      <tr className="theme4-header-row">
                        {/* S.No Header */}
                        <th style={{ width: "5%" }} className="text-center">S.No</th>

                        {/* Desktop Headers */}
                        <th className="d-none d-md-table-cell" style={{ width: "23%" }}>Products</th>
                        <th className="d-none d-md-table-cell" style={{ width: "23%" }}>பொருள்களின் பெயர்</th>
                        <th className="d-none d-md-table-cell" style={{ width: "13%" }}>Net Rate</th>
                        <th className="d-none d-md-table-cell" style={{ width: "13%" }}>Price</th>

                        {/* Mobile Combined Headers */}
                        <th className="d-table-cell d-md-none text-start ps-2" style={{ width: "42%" }}>Products / பொருள்கள்</th>
                        <th className="d-table-cell d-md-none text-center" style={{ width: "19%" }}>Price / விலை</th>

                        {/* Shared Headers */}
                        <th style={{ width: "11%" }}>Qty</th>
                        <th style={{ width: "13%" }}>Total</th>
                        <th className="d-none d-md-table-cell text-center" style={{ width: "6%" }}>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let snoCounter = 0;
                        return categories
                          .filter((cat) => cat.id !== "all" && (selectedCategory === "all" || selectedCategory === cat.id))
                          .map((cat) => {
                            const catProducts = products.filter(
                              (p) =>
                                (String(p.category_code) === String(cat.category_code) ||
                                  String(p.category_id) === String(cat.category_id) ||
                                  String(p.category_code) === String(cat.id) ||
                                  (p.category_name && p.category_name.trim().toLowerCase() === cat.name?.trim().toLowerCase()) ||
                                  (p.category && p.category.trim().toLowerCase() === cat.name?.trim().toLowerCase())) &&
                                (!cleanSearch ||
                                  (p.product_name && String(p.product_name).toLowerCase().includes(cleanSearch)) ||
                                  (p.product_name_ta && String(p.product_name_ta).toLowerCase().includes(cleanSearch)) ||
                                  String(p.product_code || "").toLowerCase().includes(cleanSearch) ||
                                  (p.category_name && p.category_name.toLowerCase().includes(cleanSearch)))
                            );
                            if (catProducts.length === 0) return null;

                            return (
                              <React.Fragment key={cat.id}>
                                <tr className="theme4-cat-header">
                                  <td colSpan="8" className="py-2.5 px-2 text-center border-0 theme4-cat-cell">
                                    <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                                      <span className="theme4-cat-title text-white fw-bold">
                                        {(cat.category_name_ta || cat.name_ta) ? `${cat.name} (${cat.category_name_ta || cat.name_ta})` : cat.name} ({catProducts.length} Products)
                                      </span>
                                      {cat.discount > 0 && (
                                        <span className="badge rounded-pill bg-warning text-dark font-weight-bold px-3 py-1 mt-1 shadow-sm" style={{ fontSize: "11px", border: "1px solid #f59e0b" }}>
                                          {cat.discount}% Category Discount Applied
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {catProducts.map((prod, prodIndex) => {
                                  snoCounter++;
                                  const cartItem = cart.find((c) => c.id === prod.id);
                                  const qty = cartItem ? cartItem.qty : 0;
                                  const mrp = parseFloat(prod.original_price || prod.price || 0);
                                  const discount = parseFloat(prod.discount || 0);
                                  const netRate = parseFloat(prod.price || 0);
                                  const lineTotal = (netRate * qty).toFixed(2);
                                  const tamilName = prod.product_name_ta || prod.name_ta || translateToTamil(String(prod.product_name || ""));

                                  const unitText = prod.unit_code || prod.unit_name || prod.unit || prod.content || prod.description || "";

                                  return (
                                    <tr key={prod.id} className="theme4-product-row">
                                      {/* S.No Cell */}
                                      <td className="fw-bold text-center align-middle">{snoCounter}</td>

                                      {/* Desktop Cells */}
                                      <td className="fw-semibold text-start ps-2 d-none d-md-table-cell">{prod.product_name}</td>
                                      <td className="fw-bold text-start ps-2 text-dark d-none d-md-table-cell">{tamilName}</td>
                                      <td className="theme4-netrate text-center align-middle text-nowrap d-none d-md-table-cell">
                                        {discount > 0 ? (
                                          <span className="text-decoration-line-through text-danger me-1">
                                            ₹{mrp.toFixed(2)}
                                          </span>
                                        ) : (
                                          <span>₹{mrp.toFixed(2)}</span>
                                        )}
                                      </td>
                                      <td className="text-center align-middle text-nowrap d-none d-md-table-cell">
                                        <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>₹{netRate.toFixed(2)}</div>
                                        {unitText && (
                                          <div className="text-secondary fw-semibold mt-0.5" style={{ fontSize: "10px" }}>
                                            {unitText}
                                          </div>
                                        )}
                                      </td>

                                      {/* Mobile Combined Cell 1: Product English + Tamil */}
                                      <td className="text-start align-middle d-table-cell d-md-none py-2 px-2.5">
                                        <div className="d-flex justify-content-between align-items-center gap-1">
                                          <div>
                                            <div className="fw-semibold text-dark mb-0.5" style={{ fontSize: "12px", lineHeight: "1.3" }}>
                                              {prod.product_name}
                                            </div>
                                            {tamilName && (
                                              <div className="text-dark fw-bold" style={{ fontSize: "11px", lineHeight: "1.3" }}>
                                                {tamilName}
                                              </div>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            className="theme4-share-btn-sm"
                                            title={`Share ${prod.product_name}`}
                                            onClick={(e) => handleShareProduct(prod, e)}
                                          >
                                            <FaShareAlt />
                                          </button>
                                        </div>
                                      </td>

                                      {/* Mobile Combined Cell 2: MRP + Selling Price + Unit */}
                                      <td className="text-center align-middle text-nowrap d-table-cell d-md-none">
                                        {discount > 0 && (
                                          <div className="text-decoration-line-through text-danger" style={{ fontSize: "10px" }}>
                                            ₹{mrp.toFixed(2)}
                                          </div>
                                        )}
                                        <div className="fw-bold text-dark" style={{ fontSize: "12px" }}>₹{netRate.toFixed(2)}</div>
                                        {unitText && (
                                          <div className="text-secondary" style={{ fontSize: "9.5px" }}>
                                            {unitText}
                                          </div>
                                        )}
                                      </td>

                                      {/* Qty Input Cell */}
                                      <td className="text-center align-middle">
                                        <input
                                          type="number"
                                          min="0"
                                          className="form-control form-control-sm theme4-qty-input mx-auto"
                                          value={qty > 0 ? qty : ""}
                                          placeholder="0"
                                          onChange={(e) => setQty(prod, e.target.value)}
                                        />
                                      </td>

                                      {/* Line Total Cell */}
                                      <td className="fw-bold text-success text-center align-middle text-nowrap" style={{ fontSize: "12px" }}>₹{lineTotal}</td>

                                      {/* Desktop Share Cell */}
                                      <td className="text-center align-middle d-none d-md-table-cell">
                                        <button
                                          type="button"
                                          className="theme4-share-btn"
                                          title={`Share ${prod.product_name}`}
                                          onClick={(e) => handleShareProduct(prod, e)}
                                        >
                                          <FaShareAlt />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ----------- PAGINATION ----------- */}
        {storeTheme !== "theme4" && totalItems > itemsPerPage && (
          <Row className="justify-content-center mt-4">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Row>
        )}

        {/* ----------- CART BAR ----------- */}
        {cart.length > 0 && (
          <div className="cart-bar">
            <div
              className="cart-info-clickable"
              onClick={() => navigate("/orderpage")}
              style={{ cursor: "pointer" }}
            >
              <span>{cartTotalItems} ITEMS</span>
              <span className="separator"> . </span>
              <span>₹ {cartTotalPrice.toFixed(2)}</span>
            </div>
            <div className="cart-action position-relative">
              <span
                className="cart-icon"
                onClick={() => navigate("/orderpage")}
                style={{ cursor: "pointer" }}
              >
                🛒
              </span>

              {cartTotalItems > 0 && (
                <span className="cart-badge">{cartTotalItems}</span>
              )}

              <Button onClick={() => navigate("/orderpage")}>View cart</Button>
            </div>
          </div>
        )}

      </Container>

      {/* Floating Action Buttons (Location, Call, WhatsApp with Animated Icon + Label) for all 4 themes */}
      {company && (
        <div className="store-fab-container">
          {/* 1. Location Action Button (Visible only if location_link is provided in store admin app) */}
          {company.location_link && company.location_link.trim() !== "" && (
            <a
              href={
                company.location_link.startsWith("http://") ||
                company.location_link.startsWith("https://")
                  ? company.location_link
                  : `https://${company.location_link}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="fab-pill-btn fab-location"
              title="View Store Location"
            >
              <div className="fab-icon-wrapper fab-icon-pulse">
                <FaMapMarkerAlt className="fab-icon" />
              </div>
              <span className="fab-label">Location</span>
            </a>
          )}

          {/* 2. Call Action Button */}
          {(company.phone || company.mobile) && (
            <a
              href={`tel:${String(company.phone || company.mobile).replace(/[^0-9+]/g, "")}`}
              className="fab-pill-btn fab-call"
              title="Call Store"
            >
              <div className="fab-icon-wrapper fab-icon-ring">
                <FaPhoneAlt className="fab-icon" />
              </div>
              <span className="fab-label">Call</span>
            </a>
          )}

          {/* 3. WhatsApp Action Button */}
          {(company.mobile || company.phone) && (
            <a
              href={`https://wa.me/${
                String(company.mobile || company.phone).replace(/[^0-9]/g, "").length === 10
                  ? `91${String(company.mobile || company.phone).replace(/[^0-9]/g, "")}`
                  : String(company.mobile || company.phone).replace(/[^0-9]/g, "")
              }?text=${encodeURIComponent(
                `Hello ${company.company_name || "Store"}, I'm browsing your online store and would like to enquire about your products.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="fab-pill-btn fab-whatsapp"
              title="Chat on WhatsApp"
            >
              <div className="fab-icon-wrapper fab-icon-bounce">
                <FaWhatsapp className="fab-icon" />
              </div>
              <span className="fab-label">WhatsApp</span>
            </a>
          )}
        </div>
      )}
          {/* Universal Share Product Modal (All 4 Themes) */}
      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        centered
        className="share-product-modal"
      >
        <Modal.Header closeButton className="border-0 pb-1">
          <Modal.Title className="fs-6 fw-bold">Share Product</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {selectedShareProduct && (
            <>
              <div className="d-flex align-items-center gap-3 p-2 bg-light rounded-3 mb-3 border">
                <img
                  src={getProductImage(selectedShareProduct)}
                  alt={selectedShareProduct.product_name}
                  style={{ width: "54px", height: "54px", objectFit: "contain", borderRadius: "8px", background: "#fff" }}
                  onError={(e) => { e.currentTarget.src = companyLogo || defaultLogo; }}
                />
                <div className="flex-grow-1 overflow-hidden">
                  <div className="fw-bold text-truncate" style={{ fontSize: "14px" }}>
                    {selectedShareProduct.product_name}
                  </div>
                  <div className="text-success fw-bold" style={{ fontSize: "13px" }}>
                    ₹{selectedShareProduct.price}
                  </div>
                  <div className="text-muted small" style={{ fontSize: "11px" }}>
                    {company?.company_name || "Zen Online Store"}
                  </div>
                </div>
              </div>

              <div className="d-grid gap-2 mb-3">
                <Button
                  variant="success"
                  className="d-flex align-items-center justify-content-center gap-2 py-2 fw-semibold"
                  style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
                  onClick={() => {
                    const shareUrl = getProductShareUrl(selectedShareProduct);
                    const storeTitle = company?.company_name || "Zen Online Store";
                    const shareText = `Check out *${selectedShareProduct.product_name}* at ₹${selectedShareProduct.price} on ${storeTitle}!

View details & order online:`;
                    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText}
${shareUrl}`)}`, '_blank');
                  }}
                >
                  <FaWhatsapp size={18} /> Share on WhatsApp
                </Button>

                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    className="w-50 d-flex align-items-center justify-content-center gap-2 py-2 small fw-semibold"
                    style={{ backgroundColor: "#1877F2", borderColor: "#1877F2" }}
                    onClick={() => {
                      const shareUrl = getProductShareUrl(selectedShareProduct);
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
                    }}
                  >
                    <FaFacebook size={16} /> Facebook
                  </Button>
                  <Button
                    variant="dark"
                    className="w-50 d-flex align-items-center justify-content-center gap-2 py-2 small fw-semibold"
                    onClick={() => {
                      const shareUrl = getProductShareUrl(selectedShareProduct);
                      const storeTitle = company?.company_name || "Zen Online Store";
                      const shareText = `Check out ${selectedShareProduct.product_name} at ₹${selectedShareProduct.price} on ${storeTitle}!`;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
                    }}
                  >
                    <FaTwitter size={16} /> X (Twitter)
                  </Button>
                </div>
              </div>

              <Form.Group>
                <Form.Label className="small text-muted fw-bold mb-1">Product Link</Form.Label>
                <div className="input-group">
                  <Form.Control
                    readOnly
                    value={getProductShareUrl(selectedShareProduct)}
                    style={{ fontSize: "12px", backgroundColor: "#f8fafc" }}
                  />
                  <Button
                    variant={copySuccess ? "success" : "outline-secondary"}
                    onClick={() => handleCopyShareLink(getProductShareUrl(selectedShareProduct))}
                    className="d-flex align-items-center gap-1 px-3"
                    style={{ fontSize: "13px" }}
                  >
                    {copySuccess ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
                  </Button>
                </div>
              </Form.Group>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Lead Magnet & Lucky Spin Wheel Popup */}
      <LeadCaptureModal
        company={company}
        API_DOMAIN={API_DOMAIN}
        subdomain={subdomain}
        hasCart={cart && cart.length > 0}
      />

    </div>
  );
};

export default Home;
